<script setup lang="ts">
import { ColorPicker } from '@/components/color-picker';
import { SvgColorPalette } from '@repo/icons';

import { type ColorPalette, useFabricStore } from '@/store/fabric.ts';

import type { FabricPath } from '@repo/style3d';
import { getFabricPathConfigure } from '@repo/style3d';

import { useRelationStore, useSelectionStore } from '@repo/stores';
import { type Shape, useStyle3d } from '@repo/style3d';
import { useParamStore } from '@/store/param.ts';
import { useRouter } from 'vue-router';
import { getImageColorChangedByMask } from '@repo/opencv';
import { type FabricShareInfo } from '@repo/style3d';
import axios, { type AxiosResponse } from 'axios';
import { getAppConfig } from '@/utils/env';
import { joinUrl } from '@repo/utils';

const defaultPalette = [
  {
    colorNo: '1',
    color: '#A9D8F6'
  },
  {
    colorNo: '2',
    color: `#CEE2E0`
  },
  {
    colorNo: '3',
    color: `#F9F0EB`
  },
  {
    colorNo: '4',
    color: `#D69F98`
  },
  {
    colorNo: '5',
    color: `#B7ABB9`
  }
];
const { OSS_BASE_URL, INDIVIDUAL_FABRIC_SHARES } = getAppConfig();
const fabricStore = useFabricStore();
const selectionStore = useSelectionStore();
const relationStore = useRelationStore();
const paramStore = useParamStore();

const router = useRouter();
const updateUrlQuery = (shape: Shape, c: Pick<ColorPalette, 'color'> & Partial<ColorPalette>, regionColors?: string[]) => {
  const { type, id } = paramStore.resourceParam.fabricResource;
  if (shape.fabric?.type === type && shape.fabric?.id === id) {
    const materialIndex = shape.materialIndex ?? 0;
    
    // 获取当前路由中的colors参数
    const currentColorsParam = router.currentRoute.value.query.colors as string | undefined;
    let colorsMap: Record<number, { colorNo?: string, color: string, regionColors?: string[] }> = {};
    
    // 如果已有colors参数，解析它
    if (currentColorsParam) {
      try {
        colorsMap = JSON.parse(decodeURIComponent(currentColorsParam));
      } catch (e) {
        console.error('[fabric-color] 解析colors参数失败:', e);
      }
    }
    
    // 更新对应materialIndex的颜色
    // 如果有区域颜色数组，则保存完整的区域颜色信息
    if (regionColors && regionColors.length > 0) {
      colorsMap[materialIndex] = {
        colorNo: c.colorNo,
        color: c.color,
        regionColors: regionColors
      };
    } else {
      colorsMap[materialIndex] = {
        colorNo: c.colorNo,
        color: c.color,
        regionColors: undefined
      };
    }
    
    // 编码并更新URL
    const colors = encodeURIComponent(JSON.stringify(colorsMap));
    router.replace({
      query: {
        ...router.currentRoute.value.query,
        colors,
        // 移除旧的color参数（向后兼容）
        color: undefined
      }
    });
    
    console.log('[fabric-color] 更新URL颜色参数:', {
      materialIndex,
      color: c,
      regionColors,
      allColors: colorsMap
    });
  }
};

const { changeFabricColor, selectMaterial, changeFabric } = useStyle3d();

const onClickColor = async (c: Pick<ColorPalette, 'color'> & Partial<ColorPalette>) => {
  const { selectedFabricId } = selectionStore;
  const found = relationStore.shapeList.find(item => item.id === selectedFabricId);
  if (selectedFabricId === '' || selectedFabricId === null || found) {
    const materialIndex = found?.materialIndex;
    const filteredList = relationStore.shapeList.filter(item => {
      if (materialIndex === undefined) {
        return item.materialIndex === 0 || item.materialIndex === undefined;
      }
      return item.materialIndex === materialIndex;
    });

    if (!filteredList?.length) {
      return;
    }

    const changeColorUrlMap: Map<string, string> = new Map();
    let finalRegionColors: string[] | undefined = undefined;

    const updatedList: Shape[] = [];
    for (const item of filteredList) {
      await selectMaterial(item.id);

      const imgUrl = item.name; //面料对应的图片
      let localChangedColorUrl: string|undefined = undefined;
      if (changeColorUrlMap.has(imgUrl)) {
        localChangedColorUrl = changeColorUrlMap.get(imgUrl);
      } else {
        //从info.json判断有无mask图,有则支持按区域变色
        let hasMask = false;
        const fabricRootPath = `${OSS_BASE_URL}${INDIVIDUAL_FABRIC_SHARES}`;
        const fabric:string|undefined = item.fabric?.id;
        if (fabric){
          const fabricRoot = joinUrl(fabricRootPath, fabric);
          const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
          hasMask = !!infoResult.data.mask.srcImage;
          if (hasMask){
            //const localUrl = await getValidImageBase64(imgUrl, "", "png"); //本地图片
            //const imgData :ImageData = await changeColor(localUrl, c.color); //本地图片变色处理
            let maskImageUrls:string[] = [];
            for (let i = 0;i < infoResult.data.mask.list.length;++i){
              maskImageUrls.push(joinUrl(fabricRoot, 'mask', infoResult.data.mask.list[i]!));
            }
            const maskInfoJsonUrl:string = joinUrl(fabricRoot, 'mask', "mask_info.json");
            
            // 先获取当前所有区域的颜色
            let strColors:string[] = [...(fabricStore.fabricInfo?.mask?.colors || infoResult.data.mask.colors!)];
            
            // 根据选中的区域索引来改变对应区域的颜色
            const selectedIndex = fabricStore.selectedRegionIndex;
            strColors[selectedIndex] = c.color;
            
            // 先更新 fabricInfo 中的颜色数组，使 fabric-region-color.vue 显示新颜色
            fabricStore.updateRegionColor(selectedIndex, c.color);
            
            // 保存最终的区域颜色数组，用于更新URL
            finalRegionColors = strColors;
            
            // 使用更新后的完整颜色数组进行整体变色
            let baseImgUrl :string = joinUrl(fabricRoot, 'mask', infoResult.data.mask.srcImage!);
            let colorChangedImage = await getImageColorChangedByMask(baseImgUrl, maskImageUrls, maskInfoJsonUrl, strColors)
            if (colorChangedImage){
              localChangedColorUrl = colorChangedImage.toDataURL();
              changeColorUrlMap.set(imgUrl, localChangedColorUrl);
            }
            else{
              hasMask = false;
            }
          }
        }
        if (!hasMask){
          localChangedColorUrl = undefined;
        }
      }
         
      if (localChangedColorUrl){
        let fabricParams: FabricPath = {
          id: item.id,
          xhrPath: localChangedColorUrl,
        };
        
        if (!item.name.endsWith('.sfab')) {
            fabricParams = { ...fabricParams, ...getFabricPathConfigure(item), path: fabricParams.xhrPath };
        }

        await changeFabric(fabricParams);
      } else {
        await changeFabricColor(c.color); //利用sdk实现变色
      }
     
      updatedList.push({ ...item, color: c.color });
    }
    relationStore.updateShape(updatedList);

    // 更新URL，传入区域颜色数组（如果有的话）
    updateUrlQuery(found || filteredList[0]!, c, finalRegionColors);

    selectionStore.setSelectedFabric('');
  } else if (selectedFabricId) {
    // 可能是钮扣之类的
    await selectMaterial(selectedFabricId);
    await changeFabricColor(c.color);
  }
};

</script>

<template>
  <div :class="$style.container">
    <div
      v-for="p of fabricStore.palette||defaultPalette"
      :key="p.colorNo"
      :style="{ backgroundColor: p.color }"
      style="width: 30px;height:30px;"
      @click="()=>onClickColor(p)"
    />
    <ColorPicker style="width: 30px;height:30px;" @confirm="(color)=>onClickColor({color})">
      <SvgColorPalette style="width: 100%; height: 100%;" />
    </ColorPicker>
  </div>
</template>

<style module>
.container {
  padding: 20px 10px 50px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
</style>
